// TypeScript types for Admin Debug Views UI
// Based on MongoDB schema from the-eye and design specification

/**
 * Search Target entity - represents a target for web scraping searches
 */
export interface ISearchTarget {
    _id: string;
    issuer?: string;
    name?: string;
    query: string;
    category?: string;
    country?: string;
    search_timestamp?: string;
    number_of_results?: number;
    source?: string;
    processed?: string;
    market?: string;
    type?: string;
    value?: number;
    currency?: string;
    bond_url?: string;
    program_url?: string;
    framework_url?: string;
    insert_date?: string;
    // Additional metadata fields that may be present
    [key: string]: unknown;
}

/**
 * Search Result item within search-results collection
 */
export interface ISearchResult {
    title?: string;
    url?: string;
    snippet?: string;
    position?: number;
    timestamp?: string;
}

/**
 * Search Attempt entity - represents a single search execution
 */
export interface ISearchAttempt {
    _id: string;
    search_target_id: string;
    query: string;
    timestamp: string;
    engine: string;
    number_of_results: number;
    max_results?: number;
    results?: ISearchResult[];
    raw_response?: Record<string, unknown>;
    insert_timestamp?: string;
    update_timestamp?: string;
    // Additional metadata fields that may be present
    [key: string]: unknown;
}

/**
 * Pagination response metadata
 */
export interface PaginationInfo {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
}

/**
 * API response for Search Targets list
 */
export interface SearchTargetsListResponse {
    searchTargets: ISearchTarget[];
    pagination: PaginationInfo;
    filters?: {
        countries: string[];
        categories: string[];
    };
}

/**
 * API response for Search Attempts list
 */
export interface SearchAttemptsListResponse {
    searchAttempts: ISearchAttempt[];
    pagination: PaginationInfo;
    filters?: {
        engines: string[];
    };
}

/**
 * Filter state for Search Targets
 */
export interface SearchTargetsFilterState {
    search?: string;
    country?: string;
    category?: string;
    fromDate?: string;
    toDate?: string;
}

/**
 * Filter state for Search Attempts
 */
export interface SearchAttemptsFilterState {
    search?: string;
    engine?: string;
    fromDate?: string;
    toDate?: string;
    targetId?: string;
}

/**
 * Query parameters for Search Targets API
 */
export interface SearchTargetsQueryParams {
    page?: number;
    limit?: number;
    search?: string;
    country?: string;
    category?: string;
    fromDate?: string;
    toDate?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
}

/**
 * Query parameters for Search Attempts API
 */
export interface SearchAttemptsQueryParams {
    page?: number;
    limit?: number;
    search?: string;
    engine?: string;
    targetId?: string;
    fromDate?: string;
    toDate?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
}

// Constants
export const SEARCH_ENGINES = [
    'bing',
    'google',
    'brave',
    'duckduckgo'
] as const;

export type SearchEngine = typeof SEARCH_ENGINES[number];

export const DEFAULT_PAGE_SIZE = 20;
export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

// Helper functions
export const formatDate = (dateString?: string): string => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
};

export const formatDateTime = (dateString?: string): string => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

export const getEngineColor = (engine?: string): string => {
    switch (engine?.toLowerCase()) {
        case 'bing':
            return '#00809d';
        case 'google':
            return '#4285f4';
        case 'brave':
            return '#fb542b';
        case 'duckduckgo':
            return '#de5833';
        default:
            return '#6b7280';
    }
};

export const truncateText = (text: string | undefined, maxLength: number): string => {
    if (!text) return '-';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
};
